import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import StorefrontHeroActions from './components/StorefrontHeroActions';
import ShopCategoryCard from './components/ShopCategoryCard';
import ShopServiceCards from './components/ShopServiceCards';
import { useProducts } from './useProducts';
import PageSeoHelmet from './PageSeoHelmet';
import { formatDisplayTitle } from './formatProductTitle';
import { categoryShelfImage } from './catalog/catalogMedia';
import { useSiteSettings } from './SiteSettingsContext';
import { getMergedStoreCopy, fillCopyTemplate } from './storeCopy';

function Shop() {
  const { settings: site } = useSiteSettings();
  const store = getMergedStoreCopy(site);
  const navigate = useNavigate();
  const [heroSearch, setHeroSearch] = useState('');
  const { categories, products, loading } = useProducts();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const storeCopy = site?.store_copy;

  const dynamicCards = useMemo(() => {
    const store = getMergedStoreCopy(site);
    const sorted = [...categories].sort(
      (a, b) => a.sort_order - b.sort_order || a.slug.localeCompare(b.slug)
    );
    return sorted
      .filter((c) => (products[c.slug] || []).length > 0)
      .map((c) => {
        const list = products[c.slug] || [];
        const img = categoryShelfImage(c, list);
        const n = list.length;
        return {
          key: c.slug,
          name: formatDisplayTitle(c.name || c.slug),
          path: `/shop/${c.slug}`,
          image: img,
          description:
            n === 1
              ? store.shop_category_desc_singular
              : fillCopyTemplate(store.shop_category_desc_template, { count: n }),
          products:
            n === 1
              ? store.shop_category_count_singular
              : fillCopyTemplate(store.shop_category_count, { count: n }),
        };
      });
  }, [categories, products, site, storeCopy]);

  const shopFeatures = Array.isArray(store.shop_features) ? store.shop_features : [];

  const submitHeroSearch = (e) => {
    e.preventDefault();
    const q = heroSearch.trim();
    if (q) navigate(`/search?q=${encodeURIComponent(q)}`);
  };

  return (
    <div className="shop-page store-page-enter">
      <PageSeoHelmet page="shop" />

      <div className="container store-page__container">
        <section className="shop-hero shop-hero--v2">
          <div className="shop-hero-panel text-center">
            <p className="shop-hero-eyebrow">{store.shop_eyebrow || 'Saloni Designs'}</p>
            <h1 className="shop-page-title">{store.shop_title || 'Handcrafted collections'}</h1>
            <p className="shop-page-subtitle shop-page-subtitle--editorial">
              {store.shop_subtitle ||
                'Rakhi, Lumba, and more — each piece made with care. Open a category to explore styles and designs.'}
            </p>
            <StorefrontHeroActions placement="shop" giftFinderPlacement="shop" />
            <form
              className="shop-hero-search mt-4"
              onSubmit={submitHeroSearch}
              role="search"
              aria-label="Search catalog"
            >
              <div className="input-group input-group-lg shadow-sm">
                <input
                  type="search"
                  className="form-control"
                  placeholder={store.shop_search_placeholder}
                  aria-label={store.shop_search_placeholder}
                  value={heroSearch}
                  onChange={(e) => setHeroSearch(e.target.value)}
                  autoComplete="off"
                />
                <button type="submit" className="btn btn-primary">
                  {store.shop_search_button}
                </button>
              </div>
            </form>
            {loading ? <p className="small text-muted mt-2 mb-0">{store.shop_loading_categories}</p> : null}
          </div>
        </section>

        <ShopServiceCards />

        <section className="shop-categories">
          <h2 className="shop-section-heading text-center">
            {store.shop_section_heading || 'Browse by category'}
          </h2>
          <p className="shop-section-lead text-center text-muted mb-4">
            {store.shop_section_lead ||
              'Tap a collection to see styles inside — kids, personalized, festive, and more.'}
          </p>
          <div className="shop-category-grid">
            {dynamicCards.map((category) => (
              <ShopCategoryCard
                key={category.key || category.name}
                to={category.path}
                image={category.image}
                name={category.name}
                description={category.description}
                countLabel={category.products}
              />
            ))}
          </div>
        </section>

        <section className="shop-features">
          <div className="row g-4">
            {shopFeatures.map((feat, i) => (
              <div className="col-md-4" key={`shop-feat-${i}`}>
                <div className="feature-card">
                  <div className="feature-icon">{feat.icon || '\u2728'}</div>
                  <h4 className="feature-title">{feat.title}</h4>
                  <p className="feature-desc">{feat.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </section>
      </div>
    </div>
  );
}

export default Shop;
